import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Phone, Save, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

export const AdminPhoneSettings = () => {
  const [adminPhoneNumber, setAdminPhoneNumber] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const { toast } = useToast();

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data: phoneData } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'admin_phone_number')
        .single();

      const { data: enabledData } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'admin_phone_enabled')
        .single();

      if (phoneData?.setting_value) {
        setAdminPhoneNumber(phoneData.setting_value as string);
      }

      if (enabledData?.setting_value) {
        setEnabled(enabledData.setting_value as boolean);
      }
    } catch (error) {
      console.error('Error loading admin phone settings:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  const saveSettings = async () => {
    if (!adminPhoneNumber.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a valid phone number.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      // Save phone number
      await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'admin_phone_number',
          setting_value: adminPhoneNumber.trim()
        }, {
          onConflict: 'setting_key'
        });

      // Save enabled status
      await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'admin_phone_enabled',
          setting_value: enabled
        }, {
          onConflict: 'setting_key'
        });

      toast({
        title: "Settings Saved",
        description: "Admin phone settings have been updated successfully.",
        variant: "default"
      });
    } catch (error) {
      console.error('Error saving admin phone settings:', error);
      toast({
        title: "Save Failed",
        description: "Failed to save settings. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (value: string) => {
    // Remove all non-digit characters
    const digits = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digits.length >= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    } else if (digits.length >= 3) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    } else {
      return digits;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setAdminPhoneNumber(formatted);
  };

  if (initialLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Phone className="w-5 h-5" />
          Admin Phone Access
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-800">Security Notice</h4>
              <p className="text-sm text-amber-700 mt-1">
                The phone number configured here will have full administrative access via SMS authentication. 
                Keep this number secure and only share with trusted personnel.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label htmlFor="enabled">Enable Admin Phone Access</Label>
              <p className="text-sm text-gray-600">Allow SMS authentication for admin access</p>
            </div>
            <Switch
              id="enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Admin Phone Number</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={adminPhoneNumber}
              onChange={handlePhoneChange}
              disabled={!enabled}
              maxLength={14}
            />
            <p className="text-sm text-gray-600">
              This phone number will receive SMS codes for admin authentication
            </p>
          </div>
        </div>

        <Button 
          onClick={saveSettings} 
          disabled={loading || !adminPhoneNumber.trim()}
          className="w-full"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </CardContent>
    </Card>
  );
};